// Function index:
//   renderUsers(host, ctx) — users panel: search + role/status filters +
//       pagination + bulk-select. Clicking a row opens the detail slide-over
//       (users/detail.ts), where all per-account actions live.

import { listAdminUsers, bulkUserAction, type AdminUser } from '../../../api.js';
import { esc, type AdminPanelCtx } from '../helpers.js';
import { openUserDetail } from './detail.js';

export async function renderUsers(host: HTMLElement, ctx: AdminPanelCtx): Promise<void> {
  let page = 1, q = '', role = '', status = '';
  const selected = new Set<number>();

  host.innerHTML = `
    <div class="user-controls" style="display:flex; gap:8px; margin-bottom:10px; flex-wrap:wrap; align-items:center;">
      <input class="user-search" placeholder="search email / name" style="width:220px;">
      <select class="user-role"><option value="">any role</option><option value="user">user</option><option value="admin">admin</option></select>
      <select class="user-status"><option value="">any status</option><option value="active">active</option><option value="disabled">disabled</option><option value="unverified">unverified</option></select>
    </div>
    <div class="user-bulk" style="display:none; margin-bottom:8px; gap:6px; align-items:center;"></div>
    <div class="user-body">Loading…</div>
    <div class="user-pager" style="margin-top:10px;"></div>
  `;

  const body = host.querySelector('.user-body') as HTMLElement;
  const pager = host.querySelector('.user-pager') as HTMLElement;
  const bulkBar = host.querySelector('.user-bulk') as HTMLElement;
  const search = host.querySelector('.user-search') as HTMLInputElement;
  const roleSel = host.querySelector('.user-role') as HTMLSelectElement;
  const statusSel = host.querySelector('.user-status') as HTMLSelectElement;

  const reload = (): void => { void load(); };

  async function load(): Promise<void> {
    body.textContent = 'Loading…';
    try {
      const res = await listAdminUsers({
        q: q || undefined,
        role: (role || undefined) as 'user' | 'admin' | undefined,
        status: (status || undefined) as 'active' | 'disabled' | 'unverified' | undefined,
        page, limit: 25,
      });
      body.innerHTML = '';
      for (const u of res.users) body.appendChild(row(u));
      renderBulkBar();
      const pages = Math.max(1, Math.ceil(res.total / res.limit));
      pager.innerHTML = `<button class="secondary u-prev" ${page <= 1 ? 'disabled' : ''}>‹ Prev</button>
        <span class="party-meta">page ${page} / ${pages} · ${res.total} users</span>
        <button class="secondary u-next" ${page >= pages ? 'disabled' : ''}>Next ›</button>`;
      pager.querySelector('.u-prev')?.addEventListener('click', () => { page--; reload(); });
      pager.querySelector('.u-next')?.addEventListener('click', () => { page++; reload(); });
    } catch (err) {
      body.textContent = '';
      ctx.setError(`users: ${(err as Error).message}`);
    }
  }

  function row(u: AdminUser): HTMLElement {
    const el = document.createElement('div');
    el.className = 'party-row';
    el.dataset.userRow = String(u.id);
    el.style.cursor = 'pointer';
    const roleBadge = u.role === 'admin' ? '<span style="color:#ffeb88;font-weight:600;">admin</span>' : 'user';
    const verified = u.email_verified ? '✓ verified' : '<span style="color:#ff8888;">unverified</span>';
    const stat = u.disabled ? '<span style="color:#ff8888;">disabled</span>' : 'active';
    el.innerHTML = `
      <div style="display:flex; gap:10px; align-items:center;">
        <input type="checkbox" class="u-sel" data-id="${u.id}" ${selected.has(u.id) ? 'checked' : ''}>
        <div>
          <div>${esc(u.email)} <span class="party-meta">· ${esc(u.display_name)}</span></div>
          <div class="party-meta">id ${u.id} · ${roleBadge} · ${verified} · ${stat}</div>
        </div>
      </div>`;
    const cb = el.querySelector('.u-sel') as HTMLInputElement;
    cb.addEventListener('click', (e) => {
      e.stopPropagation();
      if (cb.checked) selected.add(u.id); else selected.delete(u.id);
      renderBulkBar();
    });
    el.addEventListener('click', () => { void openUserDetail(u.id, ctx, reload); });
    return el;
  }

  function renderBulkBar(): void {
    if (selected.size === 0) { bulkBar.style.display = 'none'; bulkBar.innerHTML = ''; return; }
    bulkBar.style.display = 'flex';
    bulkBar.innerHTML = `<span class="party-meta">${selected.size} selected</span>
      <button class="secondary b-verify">Verify</button>
      <button class="secondary b-disable">Disable</button>
      <button class="secondary b-enable">Enable</button>
      <button class="secondary b-reset">Reset pw</button>
      <button class="secondary b-clear">Clear</button>`;
    const run = async (action: 'verify' | 'disable' | 'enable' | 'reset_password'): Promise<void> => {
      if ((action === 'disable' || action === 'reset_password') && !confirm(`${action} ${selected.size} user(s)?`)) return;
      try {
        const { results } = await bulkUserAction([...selected], action);
        const ok = results.filter((r) => r.ok).length;
        const failed = results.filter((r) => !r.ok);
        if (failed.length) {
          ctx.setError(`${ok} ok, ${failed.length} failed — ${failed.map((r) => `#${r.id}: ${r.error ?? 'failed'}`).join('; ')}`);
        } else {
          ctx.setInfo(`${ok} ok`);
        }
        selected.clear();
        reload();
      } catch (err) { ctx.setError((err as Error).message); }
    };
    bulkBar.querySelector('.b-verify')?.addEventListener('click', () => void run('verify'));
    bulkBar.querySelector('.b-disable')?.addEventListener('click', () => void run('disable'));
    bulkBar.querySelector('.b-enable')?.addEventListener('click', () => void run('enable'));
    bulkBar.querySelector('.b-reset')?.addEventListener('click', () => void run('reset_password'));
    bulkBar.querySelector('.b-clear')?.addEventListener('click', () => { selected.clear(); reload(); });
  }

  let t: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener('input', () => {
    if (t) clearTimeout(t);
    t = setTimeout(() => { q = search.value.trim(); page = 1; reload(); }, 250);
  });
  roleSel.addEventListener('change', () => { role = roleSel.value; page = 1; reload(); });
  statusSel.addEventListener('change', () => { status = statusSel.value; page = 1; reload(); });

  await load();
}
